import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";
import {
    ArrowDownLeft,
    ArrowDownRight,
    ArrowLeft,
    CheckCircle,
    Flame,
    Loader2,
    PackageSearch,
    Pencil,
    RefreshCw,
    Ruler,
    Snowflake,
    Tag,
    XCircle,
} from "lucide-react";

import { apiFetch } from "../../api/apiFetch";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import FitWord from "../FitWord";
import ScanFlashOverlay from "../ScanFlashOverlay";
import {
    ProgressBar,
    ScoreDisplay,
    TimeBonusPopup,
    useBestTime,
    useProgressTimer,
    useSessionScore,
    useStreak,
} from "../gamification";
import { playError, playGreenColdSound, playGreenHotSound, playMeasureSound, playRedSound, playSuccess } from "../helpers/audioPlayer";
import { clearRecentScans, markScanned, wasRecentlyScanned } from "../helpers/recentScans";
import { blurClickedScanPageButton, preventScanPageButtonFocus } from "../helpers/scanFocusGuard";
import greenHotImg from "../../images/green-hot.png";
import greenColdImg from "../../images/green-cold.png";
import {
    BF_COLOURS,
    BF_STATUS_TOTE_ACTIONS,
    INITIAL_BF_FLOW,
    bfApiReasonKey,
    bfFlowReducer,
    bfToteStatusMessage,
    buildBfUncommitPayload,
    coloursFor,
    findColourTote,
    findExpectedTote,
    findOverweightTote,
    hasRememberedLengthAnswer,
    isExpectedTote,
    isForcedItemClose,
    isNotOnPortal,
    isToteAtWeightLimit,
    isWeightLimitedColour,
    nextMissingColour,
    normaliseBfBarcode,
    normaliseBfMode,
    pendingFromEvent,
    requiredFor,
    resultToPanel,
    SOURCING_OVERRIDABLE,
    sourcingRefusalKey,
    routeBfConfirmScan,
    routeBfItemScan,
    toteForColour,
    toteWeightOf,
    totesForColour,
    verdictToPanel,
    type BfBookedTotals,
    type BfCheckResult,
    type BfCloseResult,
    type BfColour,
    type BfFinishSummary,
    type BfPreviousTote,
    type BfReopenMode,
    type BfErrorMessage,
    type BfTote,
    type BfToteHolder,
    type BfVerdict,
} from "./bfScanLogic";

interface BfSession {
    sessionId: number | string;
    warehouse: string;
    locationBarcode: string;
    // "standard" | "big" — the desk variant stamped on the session by the BE.
    mode?: string;
    status: string;
    startedByUid?: number | string;
    startedByName?: string;
    startedAt?: string;
    lastActivityAt?: string;
    endedAt?: string | null;
}

interface BfLocation {
    barcode: string;
    position: string;
    warehouse: string;
    bargainfox: boolean;
}

type BfCounts = Record<BfColour, number>;

interface RetryConfirm {
    result: BfCheckResult;
    toteBarcode: string;
    messageKey: string;
    continueFinish: boolean;
}

// The most recent item scan that came back from /bf-scan/check. Kept
// separately from `flow.pending` so it survives the confirm / auto-clear
// that empties the pending result — the operator can always glance down
// and see what the last unit through the desk was.
interface BfLastItem {
    // Real FNSKU (X00…) when known; "" when the scan only matched the
    // catalogue and the scanned code isn't FNSKU-shaped.
    fnsku: string;
    // What the operator actually scanned — shown in place of the FNSKU when
    // no FNSKU is known, so the readout never labels an EAN as an FNSKU.
    scanned: string;
    asin: string;
    title: string;
    imageUrl: string;
}

// Amazon FNSKUs are 10 chars starting X00 (e.g. X001T5516V).
const FNSKU_SHAPE = /^X00[0-9A-Z]{7}$/i;
const looksLikeFnsku = (code: string) => FNSKU_SHAPE.test(code.trim());

// Goods In › Sourcing runs this sheet in 'sourcing' mode against one Flow
// Sourcing PO: the session is started through /sourcing/session/start, every
// unit is matched to the PO by the BE, and a refusal (not on the PO / over
// the ordered quantity) can be accepted by a team leader.
export interface BfSourcingContext {
    ref: string;
    // The user holds goods_in.sourcing_override (Team Leader and up).
    canOverride: boolean;
    // The PO progress the Sourcing page renders under the title bar.
    banner?: ReactNode;
    // A unit was confirmed into a tote: the page refreshes its progress.
    onBooked?: () => void;
}

interface BfScanSheetProps {
    location: BfLocation;
    resumeFromCache: boolean;
    onLeave: () => void;
    onBack: () => void;
    sourcing?: BfSourcingContext;
}

// A Sourcing refusal a team leader may accept: the scan to re-send.
interface SourcingOverrideOffer {
    fnsku: string;
    reason: string;
}

const EMPTY_COUNTS: BfCounts = { HOT: 0, COLD: 0, RED: 0, MEASURE: 0 };
const DEFAULT_DURATION_MS = 6000;
const KEYBOARD_BONUS_MS = 20000;
const RED_BONUS_MS = 15000;
const ERROR_PAUSE_MS = 2000;
const ERROR_FLASH_MS = 800;
const AUTO_CLEAR_MS = 10000;
const EDIT_FEEDBACK_MS = 2500;
const USE_EMOJI_HOT_COLD = true; // Set to false to restore the Lucide flame and snowflake.

const PalletIcon = ({ size = 24 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M3 5h18v4H3z" />
        <path d="M3 11h18v4H3z" />
        <path d="M5 15v4h3v-4" />
        <path d="M10.5 15v4h3v-4" />
        <path d="M16 15v4h3v-4" />
        <path d="M3 19h18" />
    </svg>
);

const panelBackground: Record<string, string> = {
    "GREEN-HOT": "#16a34a",
    "GREEN-COLD": "#86efac",
    RED: "#FF1617",
    PURPLE: "#7B4FB5",
    BLACK: "#111111",
    "NOT-ON-PORTAL": "#111111",
    ERROR: "#000000",
    BIG: "#475569",
};

const isToteVerdict = (verdict: BfVerdict): verdict is BfColour =>
    BF_COLOURS.includes(verdict as BfColour);

const isBfColour = (value: unknown): value is BfColour =>
    typeof value === "string" && BF_COLOURS.includes(value as BfColour);

const isDeadSessionReason = (reason: unknown): reason is "no_active_session" | "session_ended" =>
    reason === "no_active_session" || reason === "session_ended";

const normaliseBookedTotals = (value: any): BfBookedTotals => ({
    HOT: Number(value?.HOT) || 0,
    COLD: Number(value?.COLD) || 0,
    RED: Number(value?.RED) || 0,
    MEASURE: Number(value?.MEASURE) || 0,
});

const normaliseOpenTotes = (value: any): BfTote[] => Array.isArray(value)
    ? value.map((tote: BfTote) => ({
        ...tote,
        units: Math.max(0, Number(tote?.units) || 0),
        weightGrams: Math.max(0, Number(tote?.weightGrams) || 0),
    }))
    : [];

const normaliseWeightLimit = (value: unknown): number | null => {
    const n = Number(value);
    return value != null && Number.isFinite(n) && n > 0 ? n : null;
};

const formatKg = (grams: number): string => {
    const kg = Math.max(0, grams) / 1000;
    return (Math.round(kg * 10) / 10).toFixed(1);
};

const BfScanSheet = ({ location, resumeFromCache, onLeave, onBack, sourcing }: BfScanSheetProps) => {
    const { t } = useTranslation();
    const [flow, dispatch] = useReducer(bfFlowReducer, INITIAL_BF_FLOW);
    const [session, setSession] = useState<BfSession | null>(null);
    const [openTotes, setOpenTotes] = useState<BfTote[]>([]);
    const [counts, setCounts] = useState<BfCounts>(EMPTY_COUNTS);
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(true);
    const [fatalKey, setFatalKey] = useState<string | null>(null);
    const [errorFlash, setErrorFlash] = useState(false);
    const [errorMessage, setErrorMessage] = useState<BfErrorMessage | null>(null);
    const [confirmationGuidance, setConfirmationGuidance] = useState(false);
    const [retryConfirm, setRetryConfirm] = useState<RetryConfirm | null>(null);
    const [lastItem, setLastItem] = useState<BfLastItem | null>(null);
    // A broken product image falls back to the tag icon rather than a
    // broken-image glyph; reset on every new item so the next one retries.
    const [lastItemImageFailed, setLastItemImageFailed] = useState(false);
    const [sessionRecoveryKey, setSessionRecoveryKey] = useState<string | null>(null);
    const [overrideOffer, setOverrideOffer] = useState<SourcingOverrideOffer | null>(null);
    const sourcingRef = sourcing?.ref ?? null;
    const onSourcingBooked = sourcing?.onBooked;
    const itemClosePromptRef = useRef(flow.itemClosePrompt);
    itemClosePromptRef.current = flow.itemClosePrompt;
    const errorTimerRef = useRef<NodeJS.Timeout | null>(null);
    const guidanceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const clearTimerRef = useRef<NodeJS.Timeout | null>(null);
    const editFeedbackTimerRef = useRef<NodeJS.Timeout | null>(null);
    const initRef = useRef(false);
    const openTotesRef = useRef<BfTote[]>([]);
    const latestConfirmRef = useRef<Promise<unknown>>(Promise.resolve());
    const reopeningRef = useRef(false);

    const timer = useProgressTimer();
    const streakApi = useStreak();
    const bestTime = useBestTime("scansheet-best-time");
    const score = useSessionScore();
    const [timeBonus, setTimeBonus] = useState<number | null>(null);
    const [timeBonusColor, setTimeBonusColor] = useState<"green" | "amber">("green");
    const [isBestTime, setIsBestTime] = useState(false);
    const [timeBonusKey, setTimeBonusKey] = useState(0);
    const bonusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const keyboardOpenRef = useRef(false);
    const suspectScanRef = useRef(false);
    const poTimersRef = useRef<Record<string, number>>({});

    useEffect(() => { openTotesRef.current = openTotes; }, [openTotes]);

    const colourLabel = useCallback((colour: BfVerdict) => t(`bf_colour_${colour.toLowerCase()}`), [t]);

    const showError = useCallback((
        messageKey: string | null = null,
        params?: Record<string, string | number>,
    ) => {
        if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
        setErrorMessage(messageKey ? { key: messageKey, params } : null);
        setErrorFlash(true);
        playError();
        timer.pauseFor(ERROR_PAUSE_MS);
        errorTimerRef.current = setTimeout(() => {
            setErrorFlash(false);
            setErrorMessage(null);
        }, messageKey ? ERROR_PAUSE_MS : ERROR_FLASH_MS);
    }, [timer]);

    // Pass the whole refusal body: an `already_open` carries who holds the
    // tote, and the message names them.
    const showToteStatusError = useCallback((data?: { reason?: string; heldBy?: BfToteHolder | null }) => {
        const message = bfToteStatusMessage(data, colour => colourLabel(colour as BfVerdict));
        showError(message?.key ?? null, message?.params);
    }, [colourLabel, showError]);

    const showConfirmationLost = useCallback(() => {
        if (guidanceTimerRef.current) clearTimeout(guidanceTimerRef.current);
        setConfirmationGuidance(true);
        guidanceTimerRef.current = setTimeout(() => setConfirmationGuidance(false), ERROR_PAUSE_MS);
        showError();
    }, [showError]);

    const readBody = async (res: Response): Promise<any> => {
        try { return await res.json(); } catch { return {}; }
    };

    const applyServerState = useCallback((data: any) => {
        const totes = normaliseOpenTotes(data?.openTotes);
        setSession(data?.session ?? null);
        setOpenTotes(totes);
        setCounts({
            HOT: Number(data?.counts?.HOT) || 0,
            COLD: Number(data?.counts?.COLD) || 0,
            RED: Number(data?.counts?.RED) || 0,
            MEASURE: Number(data?.counts?.MEASURE) || 0,
        });
        setSessionRecoveryKey(null);
        dispatch({
            type: "SESSION_READY",
            openTotes: totes,
            pending: pendingFromEvent(data?.lastEvent, totes),
            mode: normaliseBfMode(data?.session?.mode),
            weightLimitGrams: normaliseWeightLimit(data?.limits?.toteMaxWeightGrams),
        });
    }, []);

    const fetchState = useCallback(async (): Promise<boolean> => {
        const res = await apiFetch("/bf-scan/session/state");
        const data = await readBody(res);
        if (data?.none || isDeadSessionReason(data?.reason)) return false;
        if (!res.ok) throw new Error(data?.reason ?? "state");
        if (data?.session?.locationBarcode
            && normaliseBfBarcode(data.session.locationBarcode) !== normaliseBfBarcode(location.barcode)) {
            return false;
        }
        // A Sourcing session on another PO is moved to this one by a fresh start.
        if (sourcingRef && data?.session?.sourcingRef !== sourcingRef) return false;
        applyServerState(data);
        return true;
    }, [applyServerState, location.barcode, sourcingRef]);

    const startSession = useCallback(async (allowTakeover = true): Promise<void> => {
        const res = sourcingRef
            ? await apiFetch("/sourcing/session/start", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ref: sourcingRef }),
            })
            : await apiFetch("/bf-scan/session/start", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ locationBarcode: location.barcode }),
            });
        const data = await readBody(res);
        if (res.status === 409 && data?.reason === "active_session_elsewhere" && allowTakeover) {
            const elsewhere = data?.session?.locationBarcode ?? "";
            // Any totes left open by the abandoned session are booked and
            // released by the BE idle-session sweep within a minute.
            if (!window.confirm(t("bf_active_elsewhere_confirm", { location: elsewhere }))) {
                setFatalKey("bf_error_active_elsewhere");
                return;
            }
            const abandon = await apiFetch("/bf-scan/session/abandon", { method: "POST" });
            if (!abandon.ok) {
                const abandonData = await readBody(abandon);
                if (!isDeadSessionReason(abandonData?.reason)) throw new Error("abandon");
            }
            await startSession(false);
            return;
        }
        if (!res.ok) {
            if (isDeadSessionReason(data?.reason)) {
                const restored = await fetchState();
                if (!restored) setSessionRecoveryKey(bfApiReasonKey(data.reason));
                return;
            }
            setFatalKey(bfApiReasonKey(data?.reason));
            return;
        }
        setSession(data?.session ?? null);
        setSessionRecoveryKey(null);
        setOpenTotes(normaliseOpenTotes(data?.openTotes));
        const restored = await fetchState();
        if (!restored) {
            const totes = normaliseOpenTotes(data?.openTotes);
            dispatch({
                type: "SESSION_READY",
                openTotes: totes,
                mode: normaliseBfMode(data?.session?.mode),
                weightLimitGrams: normaliseWeightLimit(data?.limits?.toteMaxWeightGrams),
            });
        }
    }, [fetchState, location.barcode, sourcingRef, t]);

    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        let alive = true;
        (async () => {
            try {
                if (resumeFromCache) {
                    const restored = await fetchState();
                    if (!restored && alive) await startSession();
                } else {
                    await startSession();
                }
            } catch {
                if (alive) setFatalKey("bf_error_server");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [fetchState, resumeFromCache, startSession]);

    useEffect(() => {
        let alive = true;
        apiFetch(`/settings/${encodeURIComponent(location.warehouse)}`)
            .then(async res => res.ok ? res.json() : null)
            .then(data => {
                if (alive && data?.scan_sheet_po_timers && typeof data.scan_sheet_po_timers === "object") {
                    poTimersRef.current = data.scan_sheet_po_timers;
                }
            })
            .catch(() => { /* defaults remain active */ });
        return () => { alive = false; };
    }, [location.warehouse]);

    useEffect(() => () => {
        if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
        if (guidanceTimerRef.current) clearTimeout(guidanceTimerRef.current);
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        if (editFeedbackTimerRef.current) clearTimeout(editFeedbackTimerRef.current);
        if (bonusTimerRef.current) clearTimeout(bonusTimerRef.current);
    }, []);

    const awaitLatestConfirm = useCallback(async () => {
        try {
            await latestConfirmRef.current;
        } catch {
            // sendConfirm handles its own failures; this is a sequencing barrier only.
        }
    }, []);

    const reconcileDeadSession = useCallback(async (res: Response, data: any): Promise<boolean> => {
        if (res.ok || !isDeadSessionReason(data?.reason)) return false;

        setRetryConfirm(null);
        try {
            const restored = await fetchState();
            if (restored) return true;
        } catch {
            // The recovery screen still gives the operator a safe restart/leave path.
        }

        setSession(null);
        setSessionRecoveryKey(bfApiReasonKey(data?.reason));
        return true;
    }, [fetchState]);

    const finishPreviousTimerAndStart = (scanned: string, verdict: BfVerdict) => {
        // Sourcing is not BF Scan work and has no per-unit target yet (Gabe,
        // 2026-11-04): no countdown, no time bonus, nothing scored to bf_scan.
        if (sourcingRef) {
            suspectScanRef.current = false;
            markScanned(scanned);
            return;
        }
        const duplicate = wasRecentlyScanned(scanned);
        const suspect = suspectScanRef.current;
        suspectScanRef.current = false;
        if (timer.state.active) {
            const { elapsedMs, completed, durationMs } = timer.stop();
            if (suspect || duplicate) {
                setTimeBonus(null);
                setIsBestTime(false);
            } else if (!completed && durationMs > 0) {
                const secondsLeft = Math.max(0, Math.ceil((durationMs - elapsedMs) / 1000));
                setTimeBonusColor(elapsedMs / durationMs < 0.5 ? "green" : "amber");
                const best = bestTime.check(elapsedMs);
                setIsBestTime(best.isNewBest && best.prevExisted);
                streakApi.increment();
                score.addSeconds(secondsLeft, "bf_scan");
                setTimeBonusKey(key => key + 1);
                setTimeBonus(secondsLeft);
                if (bonusTimerRef.current) clearTimeout(bonusTimerRef.current);
                bonusTimerRef.current = setTimeout(() => {
                    setTimeBonus(null);
                    setIsBestTime(false);
                }, 1500);
            } else {
                setTimeBonus(null);
                setIsBestTime(false);
                streakApi.reset();
            }
        }
        markScanned(scanned);
        const panel = verdictToPanel(verdict);
        const custom = poTimersRef.current[panel];
        const base = typeof custom === "number" && custom > 0 ? custom : DEFAULT_DURATION_MS;
        const verdictBonus = verdict === "RED" ? RED_BONUS_MS : 0;
        timer.start(base + verdictBonus + (keyboardOpenRef.current ? KEYBOARD_BONUS_MS : 0));
    };

    const updateCount = (colour: BfColour, delta: number) => {
        setCounts(current => ({ ...current, [colour]: Math.max(0, current[colour] + delta) }));
    };

    const openTote = async (colour: BfColour, toteBarcode: string) => {
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/tote/open", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ colour, toteBarcode }),
            });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (res.status === 409 && data?.reason === "reopenable" && isBfColour(data?.colour)) {
                dispatch({
                    type: "OFFER_TOTE_REOPEN",
                    prompt: {
                        toteId: toteBarcode.trim(),
                        colour: data.colour,
                        units: Math.max(0, Number(data?.units) || 0),
                        closedAt: typeof data?.closedAt === "string" ? data.closedAt : null,
                        closedBy: typeof data?.closedBy === "string" ? data.closedBy : "",
                    },
                });
                return;
            }
            if (!res.ok) {
                showToteStatusError(data);
                return;
            }
            const opened = { ...(data?.tote as BfTote), units: Math.max(0, Number(data?.tote?.units) || 0) };
            const next = [
                ...openTotesRef.current.filter(tote => normaliseBfBarcode(tote.toteId)
                    !== normaliseBfBarcode(opened.toteId)),
                opened,
            ];
            setOpenTotes(next);
            openTotesRef.current = next;
            clearRecentScans();
            dispatch({ type: "TOTE_OPENED", openTotes: next, colour });
            playSuccess();
        } catch {
            showError();
        } finally {
            setLoading(false);
        }
    };

    const swapTote = async (colour: BfColour, toteBarcode: string) => {
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const outgoingToteId = toteForColour(openTotesRef.current, colour)?.toteId ?? "";
            const res = await apiFetch("/bf-scan/tote/swap", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ colour, newToteBarcode: toteBarcode }),
            });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (!res.ok) {
                showToteStatusError(data);
                return;
            }
            const next = [
                ...openTotesRef.current.filter(tote => normaliseBfBarcode(tote.toteId)
                    !== normaliseBfBarcode(outgoingToteId)),
                { ...(data.tote as BfTote), units: Math.max(0, Number(data?.tote?.units) || 0) },
            ];
            setOpenTotes(next);
            openTotesRef.current = next;
            const previousTote: BfPreviousTote = {
                toteId: String(data?.previousTote?.toteId ?? outgoingToteId),
                booked: data?.previousTote?.booked === true,
                units: Number(data?.previousTote?.units) || 0,
            };
            clearRecentScans();
            dispatch({ type: "TOTE_SWAPPED", previousTote, openTotes: next });
            playSuccess();
        } catch {
            showError();
        } finally {
            setLoading(false);
        }
    };

    const closeTote = async (colour: BfColour, scanned: string, source: "item" | "swap" | "finish") => {
        const tote = findColourTote(openTotesRef.current, colour, scanned);
        if (!tote) {
            showError();
            return;
        }
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/tote/close", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ colour, toteBarcode: tote.toteId }),
            });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (!res.ok) {
                if (data?.reason === "wrong_tote" && source === "finish") {
                    dispatch({ type: "FINISH_CLOSE_REJECTED" });
                }
                showToteStatusError(data);
                if (data?.reason === "no_open_tote_for_colour" || data?.reason === "already_booked") {
                    await fetchState();
                }
                return;
            }

            const next = openTotesRef.current.filter(open => normaliseBfBarcode(open.toteId)
                !== normaliseBfBarcode(tote.toteId));
            const result: BfCloseResult = {
                toteId: tote.toteId,
                booked: data?.booked === true,
                units: Number(data?.units) || 0,
                skus: Array.isArray(data?.skus) ? data.skus.length : Number(data?.skus) || 0,
            };
            setOpenTotes(next);
            openTotesRef.current = next;
            clearRecentScans();
            dispatch(source === "item"
                ? { type: "ITEM_TOTE_CLOSED", colour, openTotes: next, result }
                : source === "swap"
                    ? { type: "TOTE_CLOSED_BY_SWAP", colour, openTotes: next, result }
                    : { type: "TOTE_CLOSED", colour, openTotes: next, result });
            playSuccess();
            if (source === "finish" && next.length === 0) await completeFinishSession();
        } catch {
            showError();
        } finally {
            setLoading(false);
        }
    };

    const checkItem = async (fnsku: string, override = false) => {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        dispatch({ type: "CLEAR_BIG_FEEDBACK" });
        setRetryConfirm(null);
        setConfirmationGuidance(false);
        setOverrideOffer(null);
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/check", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(override ? { fnsku, override: true } : { fnsku }),
            });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            const sourcingKey = sourcing ? sourcingRefusalKey(data?.reason) : null;
            if (!res.ok && sourcingKey) {
                const overridable = (SOURCING_OVERRIDABLE as readonly string[]).includes(data?.reason);
                showError(overridable && !sourcing?.canOverride ? `${sourcingKey}_set_aside` : sourcingKey, {
                    ref: String(data?.ref ?? sourcing?.ref ?? ""),
                    sku: String(data?.sku ?? ""),
                    ordered: Number(data?.ordered) || 0,
                    booked: Number(data?.booked) || 0,
                });
                if (overridable && sourcing?.canOverride) setOverrideOffer({ fnsku, reason: data.reason });
                return;
            }
            if (!res.ok) {
                showError();
                if (data?.reason === "all_totes_required") {
                    await fetchState();
                }
                return;
            }
            const result = data as BfCheckResult;
            if (result.verdict === "ERROR" && !isNotOnPortal(result)) {
                dispatch({ type: "CLEAR_RESULT" });
                showError();
                return;
            }
            dispatch({ type: "CHECKED", result });
            const scanned = (result.fnsku || fnsku).trim();
            const manifestFnsku = (result.manifestFnsku ?? "").trim();
            setLastItem({
                fnsku: manifestFnsku || (looksLikeFnsku(scanned) ? scanned.toUpperCase() : ""),
                scanned,
                asin: (result.asin ?? "").trim(),
                title: (result.title ?? result.sku ?? "").trim(),
                imageUrl: (result.imageUrl ?? "").trim(),
            });
            setLastItemImageFailed(false);
            finishPreviousTimerAndStart(fnsku, result.verdict);
            playSuccess();
            if (result.verdict === "HOT") playGreenHotSound();
            if (result.verdict === "COLD") playGreenColdSound();
            if (result.verdict === "RED") playRedSound();
            if (result.verdict === "MEASURE") playMeasureSound();
            if (result.verdict === "BLACK" || isNotOnPortal(result)) {
                clearTimerRef.current = setTimeout(() => dispatch({ type: "CLEAR_RESULT" }), AUTO_CLEAR_MS);
            }
        } catch {
            showError();
        } finally {
            setLoading(false);
        }
    };

    const sendConfirm = async (
        result: BfCheckResult,
        toteBarcode: string,
        optimisticColour: BfColour,
        continueFinish = false,
    ): Promise<"confirmed" | "reclassified" | "failed"> => {
        try {
            const res = await apiFetch("/bf-scan/confirm", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ eventId: result.eventId, toteBarcode }),
            });
            const data = await readBody(res);
            if (!res.ok && isDeadSessionReason(data?.reason)) {
                updateCount(optimisticColour, -1);
                setRetryConfirm(null);
                await reconcileDeadSession(res, data);
                return "failed";
            }
            if (!res.ok) {
                updateCount(optimisticColour, -1);
                if (data?.reason === "event_not_pending") {
                    setRetryConfirm(null);
                    if (continueFinish) dispatch({ type: "CONFIRMED" });
                    showConfirmationLost();
                } else {
                    const messageKey = bfApiReasonKey(data?.reason);
                    setRetryConfirm({ result, toteBarcode, messageKey, continueFinish });
                    showError();
                }
                return "failed";
            }
            if (data?.reclassified) {
                updateCount(optimisticColour, -1);
                const nextResult: BfCheckResult = {
                    ...result,
                    ...data,
                    ok: true,
                    verdict: data.verdict,
                    expectedToteId: data.expectedToteId,
                    expectedToteIds: Array.isArray(data.expectedToteIds) ? data.expectedToteIds : undefined,
                };
                dispatch({ type: "RECLASSIFIED", result: nextResult });
                playSuccess();
                return "reclassified";
            }
            dispatch({
                type: "TOTE_UNIT_CONFIRMED",
                toteId: toteBarcode,
                weightGrams: data?.tote?.weightGrams,
            });
            setRetryConfirm(null);
            onSourcingBooked?.();
            return "confirmed";
        } catch {
            updateCount(optimisticColour, -1);
            setRetryConfirm({ result, toteBarcode, messageKey: "bf_confirm_retry", continueFinish });
            showError();
            return "failed";
        }
    };

    const reopenTote = async (mode: BfReopenMode) => {
        const prompt = flow.reopenPrompt;
        if (!prompt || flow.phase !== "reopen" || reopeningRef.current) return;

        reopeningRef.current = true;
        dispatch({ type: "CONFIRM_TOTE_REOPEN" });
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/tote/reopen", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ toteBarcode: prompt.toteId, mode }),
            });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (!res.ok) {
                dispatch({ type: "TOTE_REOPEN_FAILED" });
                showToteStatusError(data);
                return;
            }

            const returnedTote = data?.tote;
            if (!returnedTote || !isBfColour(returnedTote.colour) || !returnedTote.toteId) {
                dispatch({ type: "TOTE_REOPEN_FAILED" });
                showError();
                return;
            }
            const reopened: BfTote = {
                ...returnedTote,
                colour: returnedTote.colour,
                toteId: String(returnedTote.toteId),
                units: Math.max(0, Number(returnedTote.units) || 0),
            };
            const next = [
                ...openTotesRef.current.filter(tote => normaliseBfBarcode(tote.toteId)
                    !== normaliseBfBarcode(reopened.toteId)),
                reopened,
            ];
            setOpenTotes(next);
            openTotesRef.current = next;
            clearRecentScans();
            dispatch({ type: "TOTE_REOPENED", tote: reopened, openTotes: next });
            playSuccess();
        } catch {
            dispatch({ type: "TOTE_REOPEN_FAILED" });
            showError();
        } finally {
            reopeningRef.current = false;
            setLoading(false);
        }
    };

    const beginConfirm = (result: BfCheckResult, toteBarcode: string, optimisticColour: BfColour) => {
        const promise = sendConfirm(result, toteBarcode, optimisticColour);
        latestConfirmRef.current = promise;
        void promise.finally(() => {
            if (latestConfirmRef.current === promise) latestConfirmRef.current = Promise.resolve();
        });
    };

    const cancelPendingAsBig = async () => {
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/cancel-last", { method: "POST" });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (!res.ok) {
                dispatch({ type: "BIG_CANCEL_REJECTED" });
                showError();
                return;
            }

            if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
            setRetryConfirm(null);
            setConfirmationGuidance(false);
            dispatch({ type: "BIG_CANCELLED" });
            playSuccess();
            clearTimerRef.current = setTimeout(
                () => dispatch({ type: "CLEAR_BIG_FEEDBACK" }),
                AUTO_CLEAR_MS,
            );
        } catch {
            dispatch({ type: "BIG_CANCEL_REJECTED" });
            showError();
        } finally {
            setLoading(false);
        }
    };

    // Big mode: the operator's answer to "is this unit over the max length?"
    // for a measured-but-dimensionless SKU. Either answer is remembered per
    // SKU by the BE; YES flips the pending unit to RED before the tote scan.
    const answerLength = async (overLength: boolean) => {
        const pending = flow.pending;
        if (!pending || !flow.lengthPrompt || loading) return;
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/event/length-answer", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ eventId: pending.eventId, overLength }),
            });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (!res.ok) {
                if (data?.reason === "event_not_pending" || data?.reason === "event_not_found") {
                    dispatch({ type: "CLEAR_RESULT" });
                    showConfirmationLost();
                    return;
                }
                showError(bfApiReasonKey(data?.reason));
                return;
            }
            if (!isBfColour(data?.verdict)) {
                showError();
                return;
            }
            const next: BfCheckResult = {
                ...pending,
                ...data,
                ok: true,
                verdict: data.verdict,
                expectedToteId: data.expectedToteId ?? null,
                expectedToteIds: Array.isArray(data.expectedToteIds) ? data.expectedToteIds : undefined,
                lengthUnknown: false,
            };
            dispatch({ type: "LENGTH_ANSWERED", result: next });
            playSuccess();
            if (next.verdict === "RED") playRedSound();
        } catch {
            showError();
        } finally {
            setLoading(false);
        }
    };

    const confirmTote = async (scanned: string, continueFinish = false) => {
        const pending = flow.pending;
        if (!pending || !isToteVerdict(pending.verdict)) return;
        const route = routeBfConfirmScan(pending, openTotesRef.current, scanned);
        if (!continueFinish && route === "big") {
            await cancelPendingAsBig();
            return;
        }
        if (route !== "tote") {
            showError();
            return;
        }
        const matchedTote = findExpectedTote(pending, openTotesRef.current, scanned);
        if (!matchedTote) {
            showError();
            return;
        }
        const canonicalToteId = matchedTote.toteId;
        // The tote scan is a genuine unit of work between item scans, so the next
        // scan of the same FNSKU is a second unit and still earns its bonus.
        clearRecentScans();
        updateCount(pending.verdict, 1);
        playSuccess();
        if (!continueFinish) {
            dispatch({ type: "CONFIRMED" });
            beginConfirm(pending, canonicalToteId, pending.verdict);
            return;
        }

        setLoading(true);
        try {
            const outcome = await sendConfirm(pending, canonicalToteId, pending.verdict, true);
            if (outcome !== "confirmed") return;
            dispatch({ type: "CONFIRMED_AND_START_FINISH", openTotes: openTotesRef.current });
            if (openTotesRef.current.length === 0) await completeFinishSession();
        } finally {
            setLoading(false);
        }
    };

    const retryLastConfirm = async () => {
        if (!retryConfirm || !isToteVerdict(retryConfirm.result.verdict)) return;
        const retry = retryConfirm;
        const verdict = retry.result.verdict as BfColour;
        setRetryConfirm(null);
        updateCount(verdict, 1);
        if (!retry.continueFinish) {
            beginConfirm(retry.result, retry.toteBarcode, verdict);
            return;
        }

        setLoading(true);
        try {
            const outcome = await sendConfirm(retry.result, retry.toteBarcode, verdict, true);
            if (outcome !== "confirmed") return;
            dispatch({ type: "CONFIRMED_AND_START_FINISH", openTotes: openTotesRef.current });
            if (openTotesRef.current.length === 0) await completeFinishSession();
        } finally {
            setLoading(false);
        }
    };

    const uncommitItem = async (fnsku: string) => {
        const editScope = flow.editScope;
        if (!editScope?.toteId) return;
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/event/uncommit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(buildBfUncommitPayload(fnsku, editScope)),
            });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (res.status === 404 || res.status === 409) {
                dispatch({ type: "EDIT_REJECTED" });
                showError();
                return;
            }
            if (!res.ok || !isBfColour(data?.verdict)) {
                dispatch({ type: "EDIT_REJECTED" });
                showError();
                return;
            }

            const editedToteId = String(data?.toteId ?? editScope.toteId);
            updateCount(editScope.colour, -1);
            dispatch({ type: "EDIT_SUCCEEDED", toteId: editedToteId, weightGrams: data?.tote?.weightGrams });
            playSuccess();
            if (editFeedbackTimerRef.current) clearTimeout(editFeedbackTimerRef.current);
            editFeedbackTimerRef.current = setTimeout(() => dispatch({ type: "CLEAR_EDIT_OUTCOME" }), EDIT_FEEDBACK_MS);
        } catch {
            dispatch({ type: "EDIT_REJECTED" });
            showError();
        } finally {
            setLoading(false);
        }
    };

    const handleScan = async (scannedValue?: string) => {
        const value = (scannedValue ?? scanValue).trim();
        if (!value || loading) return;
        setScanValue("");
        // The length pop-up must be answered by button; a scan cannot stand in
        // for the operator's judgement, so it is ignored rather than errored.
        if (flow.lengthPrompt) return;
        setErrorFlash(false);
        setErrorMessage(null);
        const stagedItemClose = itemClosePromptRef.current;
        if (stagedItemClose && isForcedItemClose(stagedItemClose)) {
            // Weight limit reached: the only scan that does anything is the
            // full tote itself (close and send to put-away).
            const forcedRoute = routeBfItemScan({
                phase: "item-close",
                pending: null,
                bigFeedback: false,
                itemClosePrompt: stagedItemClose,
            }, openTotesRef.current, value);
            if (forcedRoute?.kind === "confirm-close") {
                await closeTote(forcedRoute.tote.colour, value, "item");
            } else {
                showError("bf_error_tote_weight_limit_close");
            }
            return;
        }
        if (stagedItemClose) {
            const itemRoute = routeBfItemScan({
                phase: "item-close",
                pending: null,
                bigFeedback: false,
                itemClosePrompt: stagedItemClose,
            }, openTotesRef.current, value);
            // Non-null while the session is short a tote: the prompt was
            // staged from setup (paired close), so only the short colour's
            // sibling may close and other scans are replacement totes.
            const setupOriginColour = nextMissingColour(openTotesRef.current, flow.mode);
            if (itemRoute?.kind === "stage-close") {
                if (setupOriginColour && itemRoute.tote.colour !== setupOriginColour) {
                    showToteStatusError({ reason: "already_open_here" });
                    return;
                }
                itemClosePromptRef.current = {
                    toteId: itemRoute.tote.toteId,
                    colour: itemRoute.tote.colour,
                };
                dispatch({ type: "STAGE_ITEM_TOTE_CLOSE", tote: itemRoute.tote });
            } else if (itemRoute?.kind === "confirm-close") {
                await closeTote(itemRoute.tote.colour, value, "item");
            } else if (setupOriginColour) {
                itemClosePromptRef.current = null;
                dispatch({ type: "CANCEL_ITEM_TOTE_CLOSE", openTotes: openTotesRef.current });
                await openTote(setupOriginColour, value);
            } else {
                itemClosePromptRef.current = null;
                dispatch({ type: "CANCEL_ITEM_TOTE_CLOSE", openTotes: openTotesRef.current });
                await checkItem(value);
            }
            return;
        }
        if (flow.phase === "reopen" || flow.phase === "reopen-confirming") return;
        if (flow.phase === "setup") {
            const missing = nextMissingColour(openTotesRef.current, flow.mode);
            if (!missing) return;
            // Paired close: the short colour's still-open sibling scanned
            // instead of a replacement offers to close it too (same colour
            // only — other open totes still fall through to the open error).
            const sibling = findColourTote(openTotesRef.current, missing, value);
            if (sibling) {
                itemClosePromptRef.current = { toteId: sibling.toteId, colour: sibling.colour };
                dispatch({ type: "STAGE_ITEM_TOTE_CLOSE", tote: sibling });
                return;
            }
            await openTote(missing, value);
            return;
        }
        if (flow.phase === "swap" && flow.swapColour) {
            if (totesForColour(openTotesRef.current, flow.swapColour).length === 2) {
                await closeTote(flow.swapColour, value, "swap");
            } else {
                await swapTote(flow.swapColour, value);
            }
            return;
        }
        if (flow.phase === "finish-sequence" && flow.finishColour) {
            await closeTote(flow.finishColour, value, "finish");
            return;
        }
        if (flow.phase === "finish-pending") {
            await confirmTote(value, true);
            return;
        }
        if (flow.phase === "confirm") {
            await confirmTote(value);
            return;
        }
        if (flow.phase === "edit") {
            if (flow.editScope && !flow.editScope.toteId) {
                const editTote = findColourTote(openTotesRef.current, flow.editScope.colour, value);
                if (!editTote) {
                    showError();
                    return;
                }
                dispatch({ type: "SELECT_EDIT_TOTE", toteId: editTote.toteId });
                playSuccess();
                return;
            }
            await uncommitItem(value);
            return;
        }
        const itemRoute = routeBfItemScan(flow, openTotesRef.current, value);
        if (itemRoute?.kind === "stage-close") {
            itemClosePromptRef.current = {
                toteId: itemRoute.tote.toteId,
                colour: itemRoute.tote.colour,
            };
            dispatch({ type: "STAGE_ITEM_TOTE_CLOSE", tote: itemRoute.tote });
            return;
        }
        if (itemRoute?.kind === "confirm-close") {
            await closeTote(itemRoute.tote.colour, value, "item");
            return;
        }
        await checkItem(value);
    };

    // Weight limit: once a HOT/COLD tote's confirmed weight reaches the
    // limit, force it closed before anything else scans. Derived from the
    // tote weights, so it also fires on resume and after a late confirm
    // response lands while the next unit is already pending.
    useEffect(() => {
        if (flow.phase !== "item" || flow.pending || flow.bigFeedback) return;
        const heavy = findOverweightTote(openTotes, flow);
        if (!heavy) return;
        itemClosePromptRef.current = { toteId: heavy.toteId, colour: heavy.colour, reason: "weight" };
        dispatch({ type: "STAGE_ITEM_TOTE_CLOSE", tote: heavy, reason: "weight" });
    }, [flow, openTotes]);

    useEffect(() => {
        const cancelItemCloseOnEscape = (event: KeyboardEvent) => {
            if (event.key !== "Escape" || !itemClosePromptRef.current) return;
            if (isForcedItemClose(itemClosePromptRef.current)) return;
            event.preventDefault();
            itemClosePromptRef.current = null;
            dispatch({ type: "CANCEL_ITEM_TOTE_CLOSE", openTotes: openTotesRef.current });
        };
        window.addEventListener("keydown", cancelItemCloseOnEscape);
        return () => window.removeEventListener("keydown", cancelItemCloseOnEscape);
    }, []);

    useEffect(() => {
        const cancelSpecialPhaseOnEscape = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            if (flow.phase === "edit") dispatch({ type: "EXIT_EDIT", openTotes: openTotesRef.current });
            else if (flow.phase === "reopen") {
                if (flow.reopenStartOver) dispatch({ type: "UNSTAGE_REOPEN_START_OVER" });
                else dispatch({ type: "CANCEL_TOTE_REOPEN" });
            } else if (flow.phase === "swap") dispatch({ type: "STOP_SWAP", openTotes: openTotesRef.current });
            else dispatch({ type: "CANCEL_FINISH", openTotes: openTotesRef.current });
        };
        if (flow.phase !== "edit" && flow.phase !== "reopen" && flow.phase !== "swap"
            && flow.phase !== "finish-pending" && flow.phase !== "finish-sequence") return;
        window.addEventListener("keydown", cancelSpecialPhaseOnEscape);
        return () => window.removeEventListener("keydown", cancelSpecialPhaseOnEscape);
    }, [flow.phase, flow.reopenStartOver]);

    const completeFinishSession = async () => {
        setErrorFlash(false);
        setLoading(true);
        try {
            await awaitLatestConfirm();
            const res = await apiFetch("/bf-scan/session/finish", { method: "POST" });
            const data = await readBody(res);
            if (await reconcileDeadSession(res, data)) return;
            if (!res.ok) {
                showError();
                if (data?.reason === "totes_still_open") await fetchState();
                return;
            }
            const summary: BfFinishSummary = {
                session: data?.session ?? session,
                bookedTotals: normaliseBookedTotals(data?.bookedTotals),
            };
            timer.reset();
            dispatch({ type: "FINISHED", summary });
            playSuccess();
        } catch {
            showError();
        } finally {
            setLoading(false);
        }
    };

    const startFinishSequence = async () => {
        setErrorFlash(false);
        dispatch({ type: "START_FINISH", openTotes: openTotesRef.current });
        if (!flow.pending && openTotesRef.current.length === 0) await completeFinishSession();
    };

    const abandonPendingAndStartFinish = async () => {
        setRetryConfirm(null);
        dispatch({ type: "ABANDON_PENDING_AND_START_FINISH", openTotes: openTotesRef.current });
        if (openTotesRef.current.length === 0) await completeFinishSession();
    };

    const displayResult = flow.pending;
    const panel = displayResult ? resultToPanel(displayResult) : null;
    const modeColours = coloursFor(flow.mode);
    const setupColour = flow.phase === "setup" ? nextMissingColour(openTotes, flow.mode) : null;
    // Big mode: explain a length-rule verdict under the panel — the measured
    // rule (RED only) or a remembered operator answer (RED or HOT), the
    // latter with a "Change answer" button while the unit is still pending.
    const lengthReason = flow.phase === "confirm" && !flow.lengthPrompt && displayResult
        && ((displayResult.verdict === "RED" && displayResult.reason === "over_length")
            || hasRememberedLengthAnswer(displayResult))
        ? displayResult.reason
        : null;
    const finishToteDetails = flow.finishColour ? totesForColour(openTotes, flow.finishColour) : [];
    const swapClosesTote = flow.phase === "swap" && flow.swapColour
        ? totesForColour(openTotes, flow.swapColour).length === 2
        : false;
    const stagedColour = flow.itemClosePrompt?.colour ?? flow.reopenPrompt?.colour ?? flow.finishColour
        ?? flow.swapColour ?? flow.editScope?.colour ?? setupColour;
    const activePanel = flow.bigFeedback ? "BIG" : stagedColour ? verdictToPanel(stagedColour) : panel;
    const showVerdictImage = flow.phase === "confirm" && !flow.lengthPrompt
        && (displayResult?.verdict === "HOT" || displayResult?.verdict === "COLD");
    const renderedPanel = errorFlash ? "ERROR" : activePanel;
    const darkPanel = renderedPanel === "GREEN-HOT" || renderedPanel === "RED" || renderedPanel === "PURPLE"
        || renderedPanel === "BLACK" || renderedPanel === "NOT-ON-PORTAL" || renderedPanel === "ERROR"
        || renderedPanel === "BIG";
    const showStatusStrip = flow.phase !== "loading" && flow.phase !== "finished";
    const displayLocation = sourcingRef
        ? t("sourcing_po_label", { ref: sourcingRef })
        : session?.locationBarcode || location.barcode;
    const modeBadge = sourcingRef ? t("sourcing") : t("bf_mode_title");

    const commandKey = useMemo(() => {
        if (flow.phase === "loading") return "loading";
        if (flow.bigFeedback) return "bf_verdict_big";
        if (flow.lengthPrompt) return "bf_over_length_title";
        if (flow.phase === "setup") return "bf_scan_tote";
        if (flow.phase === "item-close") return "bf_scan_tote_to_close";
        if (flow.phase === "reopen" || flow.phase === "reopen-confirming") return "bf_reopen_tote_title";
        if (flow.phase === "swap") return swapClosesTote ? "bf_scan_tote_to_close" : "bf_scan_replacement_tote";
        if (flow.phase === "edit") return flow.editScope?.toteId ? "bf_scan_unit_to_edit" : "bf_scan_edit_tote";
        if (flow.phase === "finish-pending") return "bf_finish_pending_title";
        if (flow.phase === "finish-sequence") return "bf_scan_tote_to_close";
        if (displayResult) {
            return isNotOnPortal(displayResult)
                ? "bf_verdict_not_on_portal"
                : `bf_verdict_${displayResult.verdict.toLowerCase()}`;
        }
        return "bf_scan_item";
    }, [displayResult, flow.bigFeedback, flow.editScope?.toteId, flow.lengthPrompt, flow.phase, swapClosesTote]);

    const restartSession = async () => {
        setLoading(true);
        setSessionRecoveryKey(null);
        try {
            await startSession(false);
        } catch {
            setFatalKey("bf_error_server");
        } finally {
            setLoading(false);
        }
    };

    if (sessionRecoveryKey) {
        return (
            <div className="scan-panel bf-scan-panel">
                <div className="page-title-bar">
                    <h1 className="page-title">{t("bf_mode_title")}</h1>
                </div>
                <div className="bf-fatal">
                    <XCircle size={72} />
                    <h2>{t(sessionRecoveryKey)}</h2>
                    <p>{t("bf_session_recovery_prompt")}</p>
                    <div className="bf-recovery-actions">
                        <button type="button" className="btn btn-primary" onClick={restartSession} disabled={loading}>
                            {t("bf_restart_session")}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={onLeave} disabled={loading}>
                            {t("bf_leave_mode")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (fatalKey) {
        return (
            <div className="scan-panel bf-scan-panel">
                <div className="page-title-bar">
                    <h1 className="page-title">{t("bf_mode_title")}</h1>
                    <button className="page-back-btn" onClick={onBack}><ArrowLeft size={20} /></button>
                </div>
                <div className="bf-fatal">
                    <XCircle size={72} />
                    <h2>{t(fatalKey)}</h2>
                    <button type="button" className="btn btn-primary" onClick={onLeave}>{t("bf_back_to_location")}</button>
                </div>
            </div>
        );
    }

    if (flow.phase === "finished" && flow.finishedSummary) {
        return (
            <div className="scan-panel bf-scan-panel bf-finished-panel">
                <div className="page-title-bar bf-title-bar">
                    <h1 className="page-title">{t("scan_sheet")}</h1>
                    <span className="bf-mode-badge">{modeBadge}</span>
                    <span className="bf-location-label" title={displayLocation}>{displayLocation}</span>
                </div>
                <div className="bf-finished-summary">
                    <CheckCircle size={82} />
                    <h2>{t("bf_finish_complete")}</h2>
                    <p>{t("bf_finish_summary_intro")}</p>
                    <div className="bf-summary-grid">
                        {modeColours.map(colour => (
                            <div key={colour} className={`bf-summary-card bf-status-colour--${colour.toLowerCase()}`}>
                                <span>{colourLabel(colour)}</span>
                                <strong>{flow.finishedSummary?.bookedTotals[colour] ?? 0}</strong>
                                <small>{t("bf_units_booked")}</small>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary bf-done-button"
                        onClick={() => { onLeave(); onBack(); }}
                    >
                        {t("bf_done")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`scan-panel bf-scan-panel ${renderedPanel ? "scan-panel-coloured" : ""} ${darkPanel ? "scan-light" : ""} ${renderedPanel === "ERROR" ? "scan-error" : ""}`}
            style={{ backgroundColor: renderedPanel ? panelBackground[renderedPanel] : "white" }}
            onMouseDown={preventScanPageButtonFocus}
            onClick={event => {
                blurClickedScanPageButton(event);
                if (flow.phase === "item-close"
                    && !isForcedItemClose(flow.itemClosePrompt)
                    && !(event.target as HTMLElement).closest(".bf-item-close-card")) {
                    itemClosePromptRef.current = null;
                    dispatch({ type: "CANCEL_ITEM_TOTE_CLOSE", openTotes: openTotesRef.current });
                }
            }}
        >
            <ScanFlashOverlay
                active={errorFlash}
                message={errorMessage ? t(errorMessage.key, errorMessage.params) : undefined}
            />
            <div className="page-title-bar bf-title-bar">
                <h1 className="page-title">{t("scan_sheet")}</h1>
                <span className="bf-mode-badge">{modeBadge}</span>
                <span className="bf-location-label" title={displayLocation}>{displayLocation}</span>
                {!sourcingRef && <ScoreDisplay total={score.total} />}
                {showStatusStrip && (
                    <button
                        type="button"
                        className="bf-finish-button bf-session-finish-button"
                        onClick={startFinishSequence}
                        disabled={loading || flow.lengthPrompt || flow.phase === "item-close" || flow.phase === "reopen"
                            || flow.phase === "reopen-confirming" || flow.phase === "swap" || flow.phase === "edit"
                            || flow.phase === "finish-pending" || flow.phase === "finish-sequence"}
                    >
                        <CheckCircle size={14} />
                        <span>{t(flow.closedColours.length > 0 && openTotes.length > 0
                            ? "bf_resume_finish"
                            : "bf_finish_session")}</span>
                    </button>
                )}
                {flow.phase === "setup" && !flow.backNavigationLocked && (
                    <button
                        className="page-back-btn"
                        onClick={onBack}
                        aria-label={t("back")}
                    >
                        <ArrowLeft size={20} />
                    </button>
                )}
            </div>

            {sourcing?.banner}
            {overrideOffer && (
                <div className="sourcing-override-card" role="alert">
                    <span>{t(overrideOffer.reason === "over_ordered" ? "sourcing_override_over_prompt" : "sourcing_override_offpo_prompt")}</span>
                    <div className="sourcing-override-actions">
                        <button
                            type="button"
                            className="btn btn-primary dispatch-modal-btn"
                            disabled={loading}
                            onClick={() => { void checkItem(overrideOffer.fnsku, true); }}
                        >
                            {t("sourcing_accept_anyway")}
                        </button>
                        <button type="button" className="btn btn-ghost dispatch-modal-btn" onClick={() => setOverrideOffer(null)}>
                            {t("sourcing_set_aside")}
                        </button>
                    </div>
                </div>
            )}

            <div className="bf-progress-lane">
                <ProgressBar progress={timer.state.progress} visible={timer.state.active} />
            </div>
            <TimeBonusPopup
                seconds={timeBonus}
                color={timeBonusColor}
                streak={streakApi.streak}
                isBest={isBestTime}
                bumpKey={timeBonusKey}
                darkBackground={darkPanel}
            />

            <div className="bf-content-region">
            {retryConfirm && (
                <div className="bf-retry-banner" role="alert">
                    <span>{t(retryConfirm.messageKey)}</span>
                    <button type="button" onClick={retryLastConfirm}>{t("retry")}</button>
                </div>
            )}

            {confirmationGuidance && !errorFlash && (
                <div className="bf-retry-banner bf-guidance-banner" role="alert">
                    <span>{t("bf_confirmation_lost_rescan")}</span>
                </div>
            )}

            {(flow.lastToteOutcome || flow.lastEditOutcome) && (
                <div className="bf-notice-stack" role="status">
                    {flow.lastEditOutcome && (
                        <div className="bf-success-banner">
                            {t("bf_edit_success", {
                                colour: colourLabel(flow.lastEditOutcome.colour),
                                tote: flow.lastEditOutcome.toteId,
                            })}
                        </div>
                    )}
                    {flow.lastToteOutcome?.kind === "swap" && (
                        <div className="bf-success-banner">
                            {t(flow.lastToteOutcome.tote.booked ? "bf_swap_booked_success" : "bf_swap_empty_success", {
                                tote: flow.lastToteOutcome.tote.toteId,
                                units: flow.lastToteOutcome.tote.units,
                            })}
                        </div>
                    )}
                    {flow.lastToteOutcome?.kind === "close" && (
                        <div className="bf-success-banner">
                            <span>{t(flow.lastToteOutcome.tote.booked ? "bf_close_booked_success" : "bf_close_empty_success", {
                                tote: flow.lastToteOutcome.tote.toteId,
                                units: flow.lastToteOutcome.tote.units,
                                skus: flow.lastToteOutcome.tote.skus ?? 0,
                            })}</span>
                        </div>
                    )}
                </div>
            )}

            <div className="scan-centre bf-scan-centre">
                {loading && flow.phase === "loading" ? (
                    <Loader2 className="bf-spinner" size={76} />
                ) : errorFlash ? (
                    null
                ) : (
                    <>
                        <div className="scan-command-wrap">
                            <div className="scan-command">
                                {t(commandKey).toUpperCase().split(/\s+/).filter(Boolean).map((word, i) => (
                                    <FitWord key={`${word}-${i}`}>{word}</FitWord>
                                ))}
                            </div>

                            {flow.phase === "item" && displayResult && isNotOnPortal(displayResult) && (
                                <div className="bf-prompt-detail bf-not-on-portal-detail">
                                    <span>{t("bf_not_on_portal_detail", {
                                        fnsku: displayResult.fnsku,
                                        asin: displayResult.asin ?? "",
                                    })}</span>
                                </div>
                            )}
                            {flow.lengthPrompt && displayResult && (
                                <div
                                    className="bf-reopen-card bf-length-card"
                                    role="dialog"
                                    aria-label={t("bf_over_length_title")}
                                    aria-describedby="bf-length-description"
                                >
                                    <p id="bf-length-description">
                                        {t("bf_over_length_question", { max: displayResult.maxLengthCm ?? "" })}
                                    </p>
                                    <div className="bf-reopen-actions bf-reopen-actions--choices bf-length-actions">
                                        <button
                                            type="button"
                                            className="bf-reopen-start-over bf-length-yes"
                                            onClick={() => answerLength(true)}
                                            disabled={loading}
                                        >
                                            {t("bf_over_length_yes")}
                                        </button>
                                        <button
                                            type="button"
                                            className="bf-reopen-confirm bf-length-no"
                                            onClick={() => answerLength(false)}
                                            disabled={loading}
                                        >
                                            {t("bf_over_length_no")}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {lengthReason && displayResult && (
                                <div className="bf-prompt-detail bf-length-detail">
                                    <span>{lengthReason === "over_length"
                                        ? t("bf_over_length_detail", {
                                            longest: displayResult.longestCm ?? "",
                                            max: displayResult.maxLengthCm ?? "",
                                        })
                                        : t(lengthReason === "operator_over_length"
                                            ? "bf_operator_over_length_detail"
                                            : "bf_operator_under_length_detail", {
                                            max: displayResult.maxLengthCm ?? "",
                                        })}</span>
                                    {lengthReason !== "over_length" && (
                                        <button
                                            type="button"
                                            className="bf-inline-cancel bf-length-change"
                                            onClick={() => dispatch({ type: "REOPEN_LENGTH_PROMPT" })}
                                            disabled={loading}
                                        >
                                            {t("bf_length_change_answer")}
                                        </button>
                                    )}
                                </div>
                            )}
                            {flow.phase === "setup" && setupColour && (
                                <div className="bf-prompt-detail">
                                    <span>{requiredFor(flow.mode, setupColour) > 1
                                        ? t("bf_setup_prompt_counted", {
                                            colour: colourLabel(setupColour),
                                            number: totesForColour(openTotes, setupColour).length + 1,
                                            required: requiredFor(flow.mode, setupColour),
                                        })
                                        : t(flow.closedColours.includes(setupColour) ? "bf_reopen_prompt" : "bf_setup_prompt", {
                                            colour: colourLabel(setupColour),
                                        })}</span>
                                    {requiredFor(flow.mode, setupColour) > 1
                                        && totesForColour(openTotes, setupColour).length > 0 && (
                                        <span className="bf-setup-close-hint">{t("bf_setup_close_sibling_hint", {
                                            colour: colourLabel(setupColour),
                                        })}</span>
                                    )}
                                    <strong className="bf-scan-blocked">{t("bf_items_blocked_missing_tote")}</strong>
                                </div>
                            )}
                            {flow.phase === "item-close" && flow.itemClosePrompt && (
                                <div
                                    className={`bf-reopen-card bf-item-close-card bf-status-colour--${flow.itemClosePrompt.colour.toLowerCase()}${isForcedItemClose(flow.itemClosePrompt) ? " bf-item-close-card--weight" : ""}`}
                                    role="dialog"
                                    aria-label={t(isForcedItemClose(flow.itemClosePrompt) ? "bf_tote_full_title" : "bf_scan_tote_to_close")}
                                    aria-describedby="bf-item-close-description"
                                >
                                    {isForcedItemClose(flow.itemClosePrompt) && (
                                        <strong className="bf-item-close-weight-title">{t("bf_tote_full_title")}</strong>
                                    )}
                                    <p id="bf-item-close-description">
                                        {isForcedItemClose(flow.itemClosePrompt)
                                            ? t("bf_item_close_weight", {
                                                tote: flow.itemClosePrompt.toteId,
                                                kg: formatKg(flow.toteWeights[normaliseBfBarcode(flow.itemClosePrompt.toteId)] ?? 0),
                                                limit: formatKg(flow.weightLimitGrams ?? 0),
                                                units: flow.toteUnits[normaliseBfBarcode(flow.itemClosePrompt.toteId)] ?? 0,
                                            })
                                            : t("bf_item_close_confirm", {
                                                tote: flow.itemClosePrompt.toteId,
                                                units: flow.toteUnits[normaliseBfBarcode(flow.itemClosePrompt.toteId)] ?? 0,
                                            })}
                                    </p>
                                    {!isForcedItemClose(flow.itemClosePrompt) && (
                                        <div className="bf-reopen-actions bf-item-close-actions">
                                            <button
                                                type="button"
                                                className="bf-reopen-cancel"
                                                onClick={() => {
                                                    itemClosePromptRef.current = null;
                                                    dispatch({ type: "CANCEL_ITEM_TOTE_CLOSE", openTotes: openTotesRef.current });
                                                }}
                                            >
                                                {t("cancel")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {(flow.phase === "reopen" || flow.phase === "reopen-confirming") && flow.reopenPrompt && (
                                <div
                                    className={`bf-reopen-card bf-status-colour--${flow.reopenPrompt.colour.toLowerCase()}`}
                                    role="dialog"
                                    aria-label={t("bf_reopen_tote_title")}
                                    aria-describedby="bf-reopen-description"
                                >
                                    {flow.reopenStartOver ? (
                                        <>
                                            <p id="bf-reopen-description">
                                                {t("bf_reopen_start_over_confirm", {
                                                    tote: flow.reopenPrompt.toteId,
                                                    units: flow.reopenPrompt.units,
                                                })}
                                            </p>
                                            <div className="bf-reopen-actions">
                                                <button
                                                    type="button"
                                                    className="bf-reopen-confirm bf-reopen-start-over-confirm"
                                                    onClick={() => reopenTote("start-over")}
                                                    disabled={loading || flow.phase === "reopen-confirming"}
                                                >
                                                    {flow.phase === "reopen-confirming"
                                                        ? t("loading")
                                                        : t("bf_reopen_start_over_yes")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="bf-reopen-cancel"
                                                    onClick={() => dispatch({ type: "UNSTAGE_REOPEN_START_OVER" })}
                                                    disabled={loading || flow.phase === "reopen-confirming"}
                                                >
                                                    {t("back")}
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p id="bf-reopen-description">
                                                {t("bf_reopen_tote_confirm", {
                                                    tote: flow.reopenPrompt.toteId,
                                                    units: flow.reopenPrompt.units,
                                                    closedBy: flow.reopenPrompt.closedBy || t("bf_unknown_operator"),
                                                })}
                                            </p>
                                            <div className="bf-reopen-actions bf-reopen-actions--choices">
                                                <button
                                                    type="button"
                                                    className="bf-reopen-confirm"
                                                    onClick={() => reopenTote("carry-on")}
                                                    disabled={loading || flow.phase === "reopen-confirming"}
                                                >
                                                    {flow.phase === "reopen-confirming"
                                                        ? t("loading")
                                                        : t("bf_reopen_carry_on", { units: flow.reopenPrompt.units })}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="bf-reopen-start-over"
                                                    onClick={() => {
                                                        if (flow.reopenPrompt && flow.reopenPrompt.units > 0) {
                                                            dispatch({ type: "STAGE_REOPEN_START_OVER" });
                                                        } else {
                                                            void reopenTote("start-over");
                                                        }
                                                    }}
                                                    disabled={loading || flow.phase === "reopen-confirming"}
                                                >
                                                    {t("bf_reopen_start_over")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="bf-reopen-cancel"
                                                    onClick={() => dispatch({ type: "CANCEL_TOTE_REOPEN" })}
                                                    disabled={loading || flow.phase === "reopen-confirming"}
                                                >
                                                    {t("cancel")}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {flow.phase === "swap" && flow.swapColour && (
                                <div className="bf-prompt-detail">
                                    <span>{t(swapClosesTote ? "bf_swap_close_prompt" : "bf_swap_prompt", {
                                        colour: colourLabel(flow.swapColour),
                                    })}</span>
                                    <button
                                        type="button"
                                        className="bf-inline-cancel"
                                        onClick={() => dispatch({ type: "STOP_SWAP", openTotes: openTotesRef.current })}
                                    >
                                        {t("cancel")}
                                    </button>
                                </div>
                            )}
                            {flow.phase === "finish-pending" && displayResult && isToteVerdict(displayResult.verdict) && (
                                <div className="bf-prompt-detail bf-finish-pending-prompt">
                                    <span>{t("bf_finish_pending_warning", {
                                        tote: (displayResult.expectedToteIds?.length
                                            ? displayResult.expectedToteIds
                                            : totesForColour(openTotes, displayResult.verdict).map(open => open.toteId)).join(" / "),
                                    })}</span>
                                    <button
                                        type="button"
                                        className="bf-abandon-pending-button"
                                        onClick={abandonPendingAndStartFinish}
                                        disabled={loading}
                                    >
                                        {t("bf_finish_abandon_pending")}
                                    </button>
                                    <button
                                        type="button"
                                        className="bf-inline-cancel"
                                        onClick={() => dispatch({ type: "CANCEL_FINISH", openTotes: openTotesRef.current })}
                                    >
                                        {t("cancel")}
                                    </button>
                                </div>
                            )}
                            {flow.phase === "edit" && flow.editScope && (
                                <div className="bf-prompt-detail">
                                    <span>{t(flow.editScope.toteId ? "bf_edit_unit_prompt" : "bf_edit_tote_prompt", {
                                        colour: colourLabel(flow.editScope.colour),
                                        tote: flow.editScope.toteId ?? "",
                                    })}</span>
                                    <button
                                        type="button"
                                        className="bf-inline-cancel"
                                        onClick={() => dispatch({ type: "EXIT_EDIT", openTotes: openTotesRef.current })}
                                    >
                                        {t("cancel")}
                                    </button>
                                </div>
                            )}
                            {flow.phase === "finish-sequence" && flow.finishColour && finishToteDetails.length > 0 && (
                                <div className="bf-prompt-detail">
                                    {t("bf_close_prompt", {
                                        colour: colourLabel(flow.finishColour),
                                    })}
                                    <button
                                        type="button"
                                        className="bf-inline-cancel"
                                        onClick={() => dispatch({ type: "CANCEL_FINISH", openTotes: openTotesRef.current })}
                                    >
                                        {t("cancel")}
                                    </button>
                                </div>
                            )}
                        </div>

                        {showVerdictImage && (
                            <div className="s2-inline-status bf-verdict-row">
                                {/* Big mode has no hot/cold split, so the direction arrow means nothing there. */}
                                {displayResult.verdict === "HOT" && flow.mode !== "big" && (
                                    <ArrowDownLeft
                                        className="bf-verdict-arrow bf-verdict-arrow--left"
                                        strokeWidth={2.5}
                                        aria-hidden="true"
                                        data-testid="bf-verdict-arrow-left"
                                    />
                                )}
                                <img
                                    className="scan-inline-img"
                                    src={displayResult.verdict === "HOT" ? greenHotImg : greenColdImg}
                                    alt=""
                                    aria-hidden="true"
                                />
                                {displayResult.verdict === "COLD" && (
                                    <ArrowDownRight
                                        className="bf-verdict-arrow bf-verdict-arrow--right"
                                        strokeWidth={2.5}
                                        aria-hidden="true"
                                        data-testid="bf-verdict-arrow-right"
                                    />
                                )}
                            </div>
                        )}

                        {flow.phase === "setup" && (
                            <div className="bf-setup-totes">
                                {modeColours.flatMap(colour => {
                                    const colourTotes = totesForColour(openTotes, colour);
                                    const required = requiredFor(flow.mode, colour);
                                    return Array.from({ length: required }, (_, slot) => {
                                        const tote = colourTotes[slot];
                                        return (
                                            <div
                                                key={`${colour}-${slot}`}
                                                className={`bf-setup-tote bf-setup-tote--${colour.toLowerCase()}`}
                                            >
                                                <span>{required > 1 ? `${colourLabel(colour)} ${slot + 1}` : colourLabel(colour)}</span>
                                                <strong>{tote?.toteId ?? t("bf_waiting")}</strong>
                                                {tote && <CheckCircle size={18} />}
                                            </div>
                                        );
                                    });
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            {lastItem && (
                <div className="bf-last-item" role="status" data-testid="bf-last-item">
                    {lastItem.imageUrl && !lastItemImageFailed ? (
                        <img
                            className="bf-last-item-image"
                            src={lastItem.imageUrl}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            onError={() => setLastItemImageFailed(true)}
                            data-testid="bf-last-item-image"
                        />
                    ) : (
                        <span className="bf-last-item-image bf-last-item-image--placeholder" aria-hidden="true">
                            <Tag size={14} />
                        </span>
                    )}
                    <span className="bf-last-item-label">{t("bf_last_item_label")}</span>
                    <span className="bf-last-item-ids">
                        <span className="bf-last-item-id">
                            <span className="bf-last-item-id-key">
                                {t(lastItem.fnsku ? "bf_last_item_fnsku" : "bf_last_item_scanned")}
                            </span>
                            <span className="bf-last-item-fnsku">{lastItem.fnsku || lastItem.scanned}</span>
                        </span>
                        {lastItem.asin && (
                            <span className="bf-last-item-id">
                                <span className="bf-last-item-id-key">{t("bf_last_item_asin")}</span>
                                <span className="bf-last-item-asin">{lastItem.asin}</span>
                            </span>
                        )}
                    </span>
                    <span className="bf-last-item-title" title={lastItem.title || undefined}>
                        {lastItem.title || t("bf_last_item_untitled")}
                    </span>
                </div>
            )}
            </div>

            {showStatusStrip && (
                <div className={`bf-status-strip bf-status-strip--${flow.mode}`}>
                    {modeColours.map(colour => {
                        const colourTotes = totesForColour(openTotes, colour);
                        const tote = colourTotes[0];
                        const editActive = flow.phase === "edit" && flow.editScope?.colour === colour;
                        const isActive = !!displayResult
                            && isToteVerdict(displayResult.verdict)
                            && displayResult.verdict === colour;
                        const StatusIcon = colour === "HOT" ? Flame
                            : colour === "COLD" ? Snowflake
                                : colour === "RED" ? PalletIcon
                                    : Ruler;

                        return (
                            <section
                                key={colour}
                                className={`bf-status-colour bf-status-colour--${colour.toLowerCase()}${isActive ? " bf-status-colour--active" : ""}`}
                                aria-label={colourLabel(colour)}
                            >
                                <div className="bf-status-colour-cap" aria-hidden="true" />
                                <div className="bf-status-colour-body">
                                    <div className="bf-status-heading">
                                        <span className="bf-status-heading-icon" aria-hidden="true">
                                            {USE_EMOJI_HOT_COLD && (colour === "HOT" || colour === "COLD") ? (
                                                <span className="bf-status-heading-emoji" aria-hidden="true">
                                                    {colour === "HOT" ? "🔥" : "❄️"}
                                                </span>
                                            ) : (
                                                <StatusIcon size={19} />
                                            )}
                                        </span>
                                        <strong>{colourLabel(colour)}</strong>
                                        {isActive ? (
                                            <span className="bf-status-active-pill">{t("bf_status_active")}</span>
                                        ) : (
                                            <span className="bf-status-open-count">
                                                {t("bf_status_open_count", { count: colourTotes.length })}
                                            </span>
                                        )}
                                    </div>

                                    <div className="bf-status-tote-row">
                                        {Array.from({ length: requiredFor(flow.mode, colour) }, (_, slot) => slot).map(slot => {
                                            const open = colourTotes[slot];
                                            if (!open) {
                                                return (
                                                    <div key={`free-${slot}`} className="bf-status-tote-slot bf-status-tote-slot--free">
                                                        {t("bf_status_free")}
                                                    </div>
                                                );
                                            }
                                            const units = flow.toteUnits[normaliseBfBarcode(open.toteId)]
                                                ?? Math.max(0, Number(open.units) || 0);
                                            const isDestination = isActive
                                                && !!displayResult
                                                && isExpectedTote(displayResult, openTotes, open.toteId);
                                            const weightLimited = isWeightLimitedColour(flow, colour);
                                            const weightGrams = toteWeightOf(flow, open);
                                            const overweight = weightLimited && isToteAtWeightLimit(flow, open);
                                            // Actual weight only (no "/ limit"): the tote id must
                                            // stay fully readable. The limit is on the Tote full card.
                                            const weightLabel = weightLimited
                                                ? t("bf_tote_weight", { kg: formatKg(weightGrams) })
                                                : null;
                                            return (
                                                <div
                                                    key={open.toteId}
                                                    className={`bf-status-tote-slot${isDestination ? " bf-status-tote-slot--destination" : ""}${overweight ? " bf-status-tote-slot--overweight" : ""}`}
                                                    aria-label={weightLabel
                                                        ? t("bf_tote_units_weight", { tote: open.toteId, units, weight: weightLabel })
                                                        : t("bf_tote_units", { tote: open.toteId, units })}
                                                >
                                                    <span className="bf-status-tote-id" title={open.toteId}>{open.toteId}</span>
                                                    {weightLabel && (
                                                        <span className="bf-status-tote-weight" data-testid={`bf-tote-weight-${open.toteId}`}>{weightLabel}</span>
                                                    )}
                                                    <strong className="bf-status-tote-count">{units}</strong>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="bf-status-buttons">
                                        {BF_STATUS_TOTE_ACTIONS.map(action => {
                                            const isSwap = action === "swap";
                                            const isEdit = action === "edit";
                                            const isVerify = action === "verify";
                                            const actionLabel = isSwap ? t("bf_swap_tote_tooltip") : isEdit
                                                ? t("bf_remove_item_tooltip")
                                                : t("bf_verify_tote_tooltip");
                                            // "verify" is a placeholder for the future tote
                                            // stock-check feature: always disabled.
                                            const actionDisabled = isVerify || !tote || loading || (isSwap
                                                ? flow.phase !== "item" && flow.phase !== "confirm" && flow.phase !== "edit"
                                                : flow.phase !== "item" && flow.phase !== "edit");
                                            return (
                                                <button
                                                    key={action}
                                                    type="button"
                                                    className={`bf-status-icon-button bf-status-icon-button--${action}`}
                                                    onClick={actionDisabled ? undefined : () => {
                                                        if (!tote) return;
                                                        if (isSwap) dispatch({ type: "START_SWAP", colour, openTotes: openTotesRef.current });
                                                        else if (isEdit) dispatch({ type: "START_EDIT", colour, openTotes: openTotesRef.current });
                                                    }}
                                                    disabled={actionDisabled}
                                                    aria-disabled={actionDisabled}
                                                    aria-label={actionLabel}
                                                    aria-pressed={isEdit ? editActive : undefined}
                                                    title={actionLabel}
                                                >
                                                    {isSwap ? (
                                                        <RefreshCw size={22} aria-hidden="true" />
                                                    ) : isEdit ? (
                                                        <Pencil size={22} aria-hidden="true" />
                                                    ) : (
                                                        <PackageSearch size={22} aria-hidden="true" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={handleScan}
                disabled={loading}
                placeholder={t("bf_scan_placeholder")}
                onKeyboardToggle={open => {
                    const previous = keyboardOpenRef.current;
                    keyboardOpenRef.current = open;
                    if (timer.state.active && previous !== open) timer.extend(open ? KEYBOARD_BONUS_MS : -KEYBOARD_BONUS_MS);
                }}
                onSuspectScan={() => { suspectScanRef.current = true; }}
            />
        </div>
    );
};

export default BfScanSheet;
